import { useEffect, useState, useCallback, useRef } from 'react';
import { Grid, CircularProgress, Icon, Autocomplete, TextField } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftInput from 'components/SoftInput';
import SoftButton from 'components/SoftButton';
import DataTableCustom from 'components/Tables/DataTableCustom';
import { gql, useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import * as XLSX from 'xlsx';

dayjs.locale('es');

const GET_CATEGORIAS_CONSUMOS = gql`
  query GetCategoriasConsumos {
    categoriasConsumos
  }
`;

const GET_CONSUMOS_TOTALES = gql`
  query GetConsumosTotales($fechaInicio: String, $fechaFin: String, $categoria: String, $idMateriaPrima: Int) {
    consumosTotales(fechaInicio: $fechaInicio, fechaFin: $fechaFin, categoria: $categoria, idMateriaPrima: $idMateriaPrima) {
      periodo
      rubro
      materiaPrima
      idMateriaPrima
      unidad
      saldoInicial
      costoReposicion
      ingresoPeriodo
      consumoPeriodo
      ajustes
      saldoFinalUnidades
      saldoFinalMonetario
    }
  }
`;

const GET_MATERIAS_PRIMAS = gql`
  query MateriasPrimas($search: String, $filter: JSON) {
    materiasPrimas(search: $search, filter: $filter) {
      id
      nombre
      categoria
      precio
      porcentajeUtilizable
      tipo
      disabled
      stock {
        cantidad
      }
      tipoUnidad {
        id
        nombre
        abreviatura
        multiplicador
      }
    }
  }
`;

function ReporteConsumo() {
    const defaultFechaInicio = dayjs().startOf('month').format('YYYY-MM-DD');
    const defaultFechaFin = dayjs().endOf('month').format('YYYY-MM-DD');

    const [getCategorias, { data: categoriasData }] = useLazyQuery(GET_CATEGORIAS_CONSUMOS);
    const [getConsumosTotales, { data: dataTotales, loading: loadingTotales, error: errorTotales }] = useLazyQuery(GET_CONSUMOS_TOTALES, {
        fetchPolicy: 'no-cache'
    });
    const [getMateriasPrimas, { data: materiasPrimasData, loading: loadingMateriasPrimas }] = useLazyQuery(GET_MATERIAS_PRIMAS);

    const [fechaInicio, setFechaInicio] = useState(defaultFechaInicio);
    const [fechaFin, setFechaFin] = useState(defaultFechaFin);
    const [categoria, setCategoria] = useState(null);
    const [materiaPrima, setMateriaPrima] = useState(null);
    const debounceTimerRef = useRef(null);
    const [filtrosAplicados, setFiltrosAplicados] = useState({
        fechaInicio: defaultFechaInicio,
        fechaFin: defaultFechaFin,
    });

    // Cargar categorías al montar el componente
    useEffect(() => {
        getCategorias();
    }, []);

    // Búsqueda de materias primas con debounce
    const handleSearchMateriasPrimas = useCallback((searchValue, categoriaFilter) => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        const timer = setTimeout(() => {
            const filter = {
                tipo: ["Consumible", "Plato_Ingrediente"],
                disabled: false,
                _stock: { cantidad: { _ne: null } }
            };

            if (categoriaFilter) {
                filter.categoria = categoriaFilter;
            }

            getMateriasPrimas({
                variables: {
                    search: searchValue || undefined,
                    filter
                }
            });
        }, 300);

        debounceTimerRef.current = timer;
    }, [getMateriasPrimas]);

    useEffect(() => () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    }, []);

    const handleCategoriaChange = (event, newValue) => {
        setCategoria(newValue);
        setMateriaPrima(null);

        // Buscar materias primas de esta categoría
        if (newValue) {
            handleSearchMateriasPrimas('', newValue);
        }
    };

    const handleMateriaPrimaChange = (event, newValue) => {
        setMateriaPrima(newValue);

        // Si se selecciona una materia prima, actualizar la categoría automáticamente
        if (newValue && newValue.categoria) {
            setCategoria(newValue.categoria);
        }
    };

    const handleFiltrar = () => {
        if (!fechaInicio || !fechaFin) {
            return;
        }

        const variables = {
            fechaInicio: fechaInicio || null,
            fechaFin: fechaFin || null,
            categoria: categoria || null,
            idMateriaPrima: materiaPrima ? parseInt(materiaPrima.id) : null
        };

        getConsumosTotales({ variables });
        setFiltrosAplicados({
            fechaInicio: fechaInicio || null,
            fechaFin: fechaFin || null,
        });
    };

    const error = errorTotales;

    const consumosTotalesRaw = dataTotales?.consumosTotales || [];
    const categoriasOptions = categoriasData?.categoriasConsumos || [];
    const materiasPrimasOptions = materiasPrimasData?.materiasPrimas || [];

    const totalesWithIds = consumosTotalesRaw.map((item, index) => ({
        ...item,
        id: `total-${index}`
    }));

    const formatNumeroFlexible = (valor) => {
        const numericValue = Number(valor);
        if (!Number.isFinite(numericValue)) return '0';
        return numericValue.toLocaleString('es-AR', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
        });
    };
    const formatCantidadConUnidad = (valor, unidad) => `${formatNumeroFlexible(valor)} ${unidad || ''}`.trim();
    const formatMoneda = (valor) => `$ ${formatNumeroFlexible(valor)}`;
    const getSignedTextColor = (valor) => {
        const numericValue = Number(valor) || 0;
        if (numericValue > 0) return '#2e7d32';
        if (numericValue < 0) return '#d32f2f';
        return '#6c757d';
    };
    const neutralTextColor = '#6c757d';

    const handleDescargarExcel = () => {
        if (!totalesWithIds.length) return;

        const fechaGeneracion = dayjs().format('D/M/YYYY HH:mm');
        const fechaInicioAplicada = filtrosAplicados?.fechaInicio || fechaInicio;
        const fechaFinAplicada = filtrosAplicados?.fechaFin || fechaFin;
        const fechaInicioFormateada = fechaInicioAplicada ? dayjs(fechaInicioAplicada).format('D/M/YYYY') : '';
        const fechaFinFormateada = fechaFinAplicada ? dayjs(fechaFinAplicada).format('D/M/YYYY') : '';

        const excelData = totalesWithIds.map((item) => ({
            Periodo: item.periodo || `${fechaInicioAplicada} a ${fechaFinAplicada}`,
            Rubro: item.rubro || '',
            'Materia prima': item.materiaPrima || '',
            'Saldo inicial': Number(item.saldoInicial || 0),
            'Costo de reposicion': Number(item.costoReposicion || 0),
            'Ingreso del periodo': Number(item.ingresoPeriodo || 0),
            'Consumo del periodo': Number(item.consumoPeriodo || 0),
            Ajustes: Number(item.ajustes || 0),
            'Saldo en unidades': Number(item.saldoFinalUnidades || 0),
            'Saldo final en $': Number(item.saldoFinalMonetario || 0),
            Unidad: item.unidad || '',
        }));

        const resumen = [
            ['Reporte de Consumos - Resumen'],
            ['Fecha de generacion', fechaGeneracion],
            ['Fecha inicio', fechaInicioFormateada],
            ['Fecha fin', fechaFinFormateada],
            [],
        ];

        const ws = XLSX.utils.aoa_to_sheet(resumen);
        XLSX.utils.sheet_add_json(ws, excelData, {
            origin: 'A7',
            skipHeader: false,
        });

        ws['!cols'] = [
            { wch: 26 },
            { wch: 26 },
            { wch: 36 },
            { wch: 18 },
            { wch: 20 },
            { wch: 20 },
            { wch: 20 },
            { wch: 14 },
            { wch: 24 },
            { wch: 18 },
            { wch: 10 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Reporte Consumo');

        const timestampArchivo = dayjs().format('YYYY-MM-DD_HH-mm-ss');
        XLSX.writeFile(wb, `Reporte_Consumo_${timestampArchivo}.xlsx`);
    };

    const columnasConsumosGenerales = [
        {
            field: 'rubro',
            headerName: 'Rubro',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="regular" color="dark" title={row.rubro}>
                    {row.rubro}
                </SoftTypography>
            )
        },
        {
            field: 'materiaPrima',
            headerName: 'Materia Prima',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="regular" color="dark" title={row.materiaPrima}>
                    {row.materiaPrima}
                </SoftTypography>
            )
        },
        {
            field: 'saldoInicial',
            headerName: 'Saldo Inicial',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    sx={{ color: getSignedTextColor(row.saldoInicial) }}
                    title={formatCantidadConUnidad(row.saldoInicial, row.unidad)}
                >
                    {formatCantidadConUnidad(row.saldoInicial, row.unidad)}
                </SoftTypography>
            )
        },
        {
            field: 'costoReposicion',
            headerName: 'Costo de reposicion',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    sx={{ color: neutralTextColor }}
                    title={formatMoneda(row.costoReposicion)}
                >
                    {formatMoneda(row.costoReposicion)}
                </SoftTypography>
            )
        },
        {
            field: 'ingresoPeriodo',
            headerName: 'Ingreso del periodo',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    sx={{ color: getSignedTextColor(row.ingresoPeriodo) }}
                    title={formatCantidadConUnidad(row.ingresoPeriodo, row.unidad)}
                >
                    {formatCantidadConUnidad(row.ingresoPeriodo, row.unidad)}
                </SoftTypography>
            )
        },
        {
            field: 'consumoPeriodo',
            headerName: 'Consumo del periodo',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography variant="caption" fontWeight="regular" color="dark" title={formatCantidadConUnidad(row.consumoPeriodo, row.unidad)}>
                    {formatCantidadConUnidad(row.consumoPeriodo, row.unidad)}
                </SoftTypography>
            )
        },
        {
            field: 'ajustes',
            headerName: 'Ajuste',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    sx={{ color: getSignedTextColor(row.ajustes) }}
                    title={formatCantidadConUnidad(row.ajustes, row.unidad)}
                >
                    {formatCantidadConUnidad(row.ajustes, row.unidad)}
                </SoftTypography>
            )
        },
        {
            field: 'saldoFinalUnidades',
            headerName: 'Saldo en unidades',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    sx={{ color: getSignedTextColor(row.saldoFinalUnidades) }}
                    title={formatCantidadConUnidad(row.saldoFinalUnidades, row.unidad)}
                >
                    {formatCantidadConUnidad(row.saldoFinalUnidades, row.unidad)}
                </SoftTypography>
            )
        },
        {
            field: 'saldoFinalMonetario',
            headerName: 'Saldo en $',
            type: 'number',
            sortable: false,
            renderCell: (row) => (
                <SoftTypography
                    variant="caption"
                    fontWeight="regular"
                    sx={{ color: getSignedTextColor(row.saldoFinalMonetario) }}
                    title={formatMoneda(row.saldoFinalMonetario)}
                >
                    {formatMoneda(row.saldoFinalMonetario)}
                </SoftTypography>
            )
        }
    ];


    if (error) return (
        <Grid container textAlign={"center"} py={3}>
            <Grid item xs={12}>
                <Icon sx={{ width: "50px" }}>warning</Icon>
            </Grid>
            <Grid item xs={12}>
                <SoftTypography variant="h6">Error al cargar los datos</SoftTypography>
            </Grid>
            <Grid item xs={12}>
                <SoftTypography variant="caption" component="p" color="text">
                    {error.message}
                </SoftTypography>
            </Grid>
        </Grid>
    );

    return (
        <SoftBox mt={2} p={2}>
            <SoftBox mb={3}>
                <SoftTypography variant="h5" fontWeight="bold" color="dark">
                    Reporte de Consumo
                </SoftTypography>
            </SoftBox>

            <SoftBox mb={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <SoftTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                    Filtros
                </SoftTypography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={2.4}>
                        <SoftTypography variant="caption" fontWeight="bold">Fecha Inicio</SoftTypography>
                        <SoftInput type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                        <SoftTypography variant="caption" fontWeight="bold">Fecha Fin</SoftTypography>
                        <SoftInput type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={2.4} mt={1}>
                        <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Categoría / Rubro</SoftTypography>
                        <Autocomplete
                            options={categoriasOptions}
                            value={categoria}
                            onChange={handleCategoriaChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Seleccione una categoría"
                                    variant="outlined"
                                />
                            )}
                        // sx={{ '& .MuiOutlinedInput-root': { height: '40px', padding: '0 !important' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2.4} mt={1}>
                        <SoftTypography variant="caption" fontWeight="bold" mb={1} display="block">Materia Prima</SoftTypography>
                        <Autocomplete
                            options={materiasPrimasOptions}
                            filterOptions={(options) => options}
                            getOptionLabel={(option) => option.nombre || ''}
                            value={materiaPrima}
                            onChange={handleMateriaPrimaChange}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason !== 'input') return;
                                handleSearchMateriasPrimas(newInputValue?.trim(), categoria);
                            }}
                            loading={loadingMateriasPrimas}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option.nombre}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Busque una materia prima"
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingMateriasPrimas ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        // sx={{ '& .MuiOutlinedInput-root': { minHeight: '40px', padding: '0 !important' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={2.4}>
                        <SoftBox mt={3}>
                            <SoftButton
                                variant="gradient"
                                color="info"
                                onClick={handleFiltrar}
                                fullWidth
                                disabled={!fechaInicio || !fechaFin}
                            >
                                Filtrar
                            </SoftButton>
                        </SoftBox>
                    </Grid>
                </Grid>
            </SoftBox>

            <SoftBox mb={4} mt={4}>
                <SoftBox
                    mb={2}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                >
                    <SoftTypography variant="h6" fontWeight="bold" color="dark">Consumos Generales</SoftTypography>
                    <SoftButton
                        variant="gradient"
                        color="success"
                        onClick={handleDescargarExcel}
                        disabled={loadingTotales || !totalesWithIds.length}
                    >
                        <Icon sx={{ mr: 0.5 }}>download</Icon>
                        Excel
                    </SoftButton>
                </SoftBox>
                {loadingTotales ? (
                    <SoftBox display="flex" justifyContent="center" p={3}><CircularProgress /></SoftBox>
                ) : (
                    <DataTableCustom
                        columns={columnasConsumosGenerales}
                        data={totalesWithIds}
                        showActions={false}
                        zebraStripes={true}
                        border={true}
                        headerStyles={{
                            backgroundColor: "#ffffff",             // fondo blanco
                            borderBottom: "1.5px solid #070606ff",
                            py: 1.1,                               // menos alto
                            "& .MuiTypography-root": {
                                fontWeight: 600,                      // negrita media
                                color: "#344767",                     // gris azulado
                                // textTransform: "none",                // sin uppercase
                                fontSize: "0.7rem",
                                letterSpacing: 0.2,
                            },
                        }}
                    />
                )}
            </SoftBox>
        </SoftBox>
    );
}

export default ReporteConsumo;


